'use client';

import { useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

export type LeadStage = 'New' | 'Contacted' | 'Interested' | 'Counseling Scheduled' | 'Applied' | 'Converted' | 'Lost';

export interface Lead {
  id: string;
  name: string;
  phone: string;
  email: string;
  neetScore: number;
  source: string;
  stage: LeadStage;
  counselor: string;
  budget: string;
  location: string;
  lastActivity: string;
  duplicate: boolean;
  optedIn: boolean;
  createdAt: string;
  externalUid: string | null;
  checkpointGmc: string | null;
  checkpointServices: string | null;
  checkpointReport: string | null;
  checkpointInsight: string | null;
  checkpointSupport: string | null;
}

function mapRow(row: Record<string, unknown>): Lead {
  return {
    id: row.id as string,
    name: row.name as string,
    phone: row.phone as string,
    email: (row.email as string) ?? '',
    neetScore: (row.neet_score as number) ?? 0,
    source: row.source as string,
    stage: row.stage as LeadStage,
    counselor: (row.counselor as string) ?? 'Unassigned',
    budget: (row.budget as string) ?? '',
    location: (row.location as string) ?? 'India',
    lastActivity: (row.last_activity as string) ?? 'Just now',
    duplicate: (row.is_duplicate as boolean) ?? false,
    optedIn: (row.opted_in as boolean) ?? true,
    createdAt: row.created_at as string,
    externalUid: (row.external_uid as string) ?? null,
    checkpointGmc: (row.checkpoint_gmc as string) ?? null,
    checkpointServices: (row.checkpoint_services as string) ?? null,
    checkpointReport: (row.checkpoint_report as string) ?? null,
    checkpointInsight: (row.checkpoint_insight as string) ?? null,
    checkpointSupport: (row.checkpoint_support as string) ?? null,
  };
}

export function useRealtimeLeads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLeads = useCallback(async () => {
    const supabase = createClient();
    const { data, error: fetchError } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });

    if (fetchError) {
      setError(fetchError.message);
    } else {
      setLeads((data ?? []).map(mapRow));
    }
    setLoading(false);
  }, []);

  const updateLeadStage = useCallback(async (leadId: string, newStage: LeadStage) => {
    const supabase = createClient();
    const { error: updateError } = await supabase
      .from('leads')
      .update({ stage: newStage, last_activity: 'Just now' })
      .eq('id', leadId);
    return updateError;
  }, []);

  useEffect(() => {
    fetchLeads();

    const supabase = createClient();
    const channel = supabase
      .channel('leads_realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'leads' },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setLeads((prev) => {
              const newLead = mapRow(payload.new as Record<string, unknown>);
              // Insert at the correct position based on created_at (newest first)
              const newTime = newLead.createdAt ? new Date(newLead.createdAt).getTime() : 0;
              const insertIdx = prev.findIndex((l) => {
                const t = l.createdAt ? new Date(l.createdAt).getTime() : 0;
                return newTime >= t;
              });
              if (insertIdx === -1) return [...prev, newLead];
              const next = [...prev];
              next.splice(insertIdx, 0, newLead);
              return next;
            });
          } else if (payload.eventType === 'UPDATE') {
            setLeads((prev) =>
              prev.map((l) =>
                l.id === (payload.new as Record<string, unknown>).id
                  ? mapRow(payload.new as Record<string, unknown>)
                  : l
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setLeads((prev) =>
              prev.filter((l) => l.id !== (payload.old as Record<string, unknown>).id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchLeads]);

  return { leads, loading, error, updateLeadStage, refetch: fetchLeads };
}
